import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";

export const INDUSTRIES_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export interface IndustryListItem {
  _id: string;
  id?: string;
  name?: string;
  name_ar?: string;
  status?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface IndustriesState {
  list: IndustryListItem[];
  lastFetchedAt: number | null;
}

const initialState: IndustriesState = {
  list: [],
  lastFetchedAt: null,
};

export const industriesSlice = createSlice({
  name: "industries",
  initialState,
  reducers: {
    setIndustries: (
      state,
      action: PayloadAction<{ list: IndustryListItem[]; lastFetchedAt: number }>
    ) => {
      state.list = action.payload.list;
      state.lastFetchedAt = action.payload.lastFetchedAt;
    },
    addIndustry: (state, action: PayloadAction<IndustryListItem>) => {
      const item = {
        ...action.payload,
        id: action.payload._id ?? action.payload.id,
      };
      state.list.push(item);
    },
    updateIndustry: (
      state,
      action: PayloadAction<{ id: string; data: Partial<IndustryListItem> }>
    ) => {
      const index = state.list.findIndex((s) => s._id === action.payload.id);
      if (index !== -1) {
        state.list[index] = {
          ...state.list[index],
          ...action.payload.data,
          _id: action.payload.id,
          id: action.payload.id,
        };
      }
    },
    removeIndustry: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((s) => s._id !== action.payload);
    },
    clearIndustries: (state) => {
      state.list = [];
      state.lastFetchedAt = null;
    },
  },
});

export const {
  setIndustries,
  addIndustry,
  updateIndustry,
  removeIndustry,
  clearIndustries,
} = industriesSlice.actions;

export const selectIndustriesList = (state: RootState) => state.industries.list;
export const selectIndustriesLastFetchedAt = (state: RootState) =>
  state.industries.lastFetchedAt;
export const selectIsIndustriesCacheValid = (state: RootState): boolean => {
  const last = state.industries.lastFetchedAt;
  if (last == null) return false;
  return Date.now() - last < INDUSTRIES_CACHE_TTL_MS;
};

export default industriesSlice.reducer;
